import React from 'react';
import { Box, Flex, Heading, SimpleGrid, Spinner, Text, Stack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Calendar,
  FileText,
  Buildings,
  Bank,
  Scales,
  ListChecks,
} from 'phosphor-react';
import { MdAssessment } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../components/glass';
import { usePermittedSubmodules } from '../../../utils/getSubmodulesForPath';
import { useFechamentos } from '../../../hooks/useFechamentos';
import { IPrivateRoutes } from '../../../routes/private.routes';
import { FECHAMENTO_TOTALIZADORES_DEFAULT_KEY } from './fechamentos/helpers';

const MODULE_NAME = 'Contábil';
const MODULE_DESCRIPTION =
  'Conciliação bancária, fechamentos contábeis, integração fiscal-contábil e exportação para o Domínio. Tudo num só lugar.';
const MODULE_COLOR = '#FFE1C6';
const MODULE_ICON_COLOR = '#F2994A';
const BASE_PATH = '/modulesHome/accountingConciliation';

/**
 * Mapeamento de submodulo -> icone exibido no card.
 * Caso o submodulo nao esteja aqui, usa um icone padrao.
 */
const SUBMODULE_ICONS: Record<string, React.ReactNode> = {
  '/modulesHome/accountingConciliation/fechamentos': (
    <Calendar size={26} color="#22875A" weight="duotone" />
  ),
  '/modulesHome/accountingConciliation/fiscalcontabil': <MdAssessment size={26} color="#008CCC" />,
  '/modulesHome/accountingConciliation/conciliacao-contabil': (
    <Bank size={26} color="#6366F1" weight="duotone" />
  ),
  '/modulesHome/accountingConciliation/balancete': (
    <Scales size={26} color="#7C5CE0" weight="duotone" />
  ),
  '/modulesHome/accountingConciliation/fechamento-contabil-mensal': (
    <ListChecks size={26} color="#0F766E" weight="duotone" />
  ),
};

const SUBMODULE_TINT: Record<string, string> = {
  '/modulesHome/accountingConciliation/fechamentos': '#E0F5E5',
  '/modulesHome/accountingConciliation/fiscalcontabil': '#DFF3FF',
  '/modulesHome/accountingConciliation/conciliacao-contabil': '#E0E7FF',
  '/modulesHome/accountingConciliation/balancete': '#EDE9FE',
  '/modulesHome/accountingConciliation/fechamento-contabil-mensal': '#CCFBF1',
};

// Descricao curta exibida no card do submodulo. Permite extender mostrando
// preview component sem if/else hardcoded em SubmoduleCard.
const SUBMODULE_DESCRIPTION: Record<string, string> = {
  '/modulesHome/accountingConciliation/fechamentos':
    'Status contábil por empresa, baseado em cliente desde/até',
  '/modulesHome/accountingConciliation/fiscalcontabil':
    'Notas fiscais e divergências de lançamento contábil',
  '/modulesHome/accountingConciliation/conciliacao-contabil':
    'Conciliação bancária: importe extratos, vincule POC e exporte para o Domínio',
  '/modulesHome/accountingConciliation/balancete':
    'Saldos por conta na ordem do plano de contas — clique na analítica e abra o papel de trabalho',
  '/modulesHome/accountingConciliation/fechamento-contabil-mensal':
    'Revisão do fechamento por competência: parecer, etapas e o que falta para liberar',
};

// Componente de preview opcional renderizado dentro do card. Map por path:
// novos submodulos podem registrar aqui sem editar SubmoduleCard.
const SUBMODULE_PREVIEW: Record<string, React.ComponentType> = {
  '/modulesHome/accountingConciliation/fechamentos': () => <FechamentosPreview />,
};

const DEFAULT_ICON = <FileText size={26} color="#1B88F5" weight="duotone" />;

// ============================================================================
// Card Submodulo
// ============================================================================

interface SubmoduleCardProps {
  sub: IPrivateRoutes;
  index: number;
}

const SubmoduleCard = ({ sub, index }: SubmoduleCardProps) => {
  const navigate = useNavigate();
  const description = SUBMODULE_DESCRIPTION[sub.path] ?? 'Acesse o submódulo';
  const PreviewComponent = SUBMODULE_PREVIEW[sub.path];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.08 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <GlassCard
        intensity="strong"
        hoverable
        p={6}
        cursor="pointer"
        onClick={() => navigate(sub.path)}
        h="full"
      >
        <Flex direction="column" gap={4} h="full">
          <Flex align="center" justify="space-between">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="52px"
              h="52px"
              borderRadius="2xl"
              bg={SUBMODULE_TINT[sub.path] ?? '#DFF3FF'}
              flexShrink={0}
            >
              {SUBMODULE_ICONS[sub.path] ?? DEFAULT_ICON}
            </Box>
            <Box
              opacity={0.5}
              transition="all 0.2s ease"
              _groupHover={{ opacity: 1, transform: 'translateX(2px)' }}
            >
              <ArrowRight size={18} color="#1B88F5" weight="bold" />
            </Box>
          </Flex>

          <Box flex={1}>
            <Heading as="h3" fontSize="md" fontWeight="600" color="#0F172A" lineHeight="1.3" mb={1}>
              {sub.name}
            </Heading>
            <Text fontSize="xs" color="#71717A">
              {description}
            </Text>
          </Box>

          {PreviewComponent && <PreviewComponent />}
        </Flex>
      </GlassCard>
    </motion.div>
  );
};

// ============================================================================
// Preview com KPIs do Fechamentos
// ============================================================================

const FechamentosPreview = () => {
  const { getTotalizadores } = useFechamentos();
  const { data, isFetching, isError } = useQuery({
    // Constante compartilhada com FechamentosPage pra garantir cache hit
    // ao navegar do preview pra pagina cheia.
    queryKey: FECHAMENTO_TOTALIZADORES_DEFAULT_KEY,
    queryFn: getTotalizadores,
    staleTime: 5 * 60 * 1000,
  });

  const t = data?.data;

  if (isFetching && !t) {
    return (
      <Flex justify="center" py={3}>
        <Spinner size="sm" color="#1B88F5" thickness="2px" />
      </Flex>
    );
  }

  // Erro silencioso (timeout, 500, etc) — exibe aviso curto em vez de
  // sumir com o preview sem feedback.
  if (isError) {
    return (
      <Box mt={1} pt={3} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
        <Text fontSize="10px" color="red.500">
          Falha ao carregar resumo. Tente novamente.
        </Text>
      </Box>
    );
  }

  if (!t) return null;

  return (
    <Box mt={1} pt={4} borderTopWidth="1px" borderColor="rgba(15, 23, 42, 0.06)">
      <SimpleGrid columns={3} spacing={3}>
        <Box>
          <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="medium">
            Total
          </Text>
          <Text fontSize="lg" fontWeight="700" color="#0F172A">
            {t.total_empresas}
          </Text>
        </Box>
        <Box>
          <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="medium">
            Em dia
          </Text>
          <Text fontSize="lg" fontWeight="700" color="#22C55E">
            {t.pct_contabil_ok.toFixed(0)}%
          </Text>
        </Box>
        <Box>
          <Text fontSize="9px" color="#94A3B8" textTransform="uppercase" fontWeight="medium">
            Pendentes
          </Text>
          <Text fontSize="lg" fontWeight="700" color="#F59E0B">
            {t.contabil_pendente + t.contabil_encerramento_pendente}
          </Text>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

// ============================================================================
// Pagina principal
// ============================================================================

export function ModulesHomeAccountingConciliationPage() {
  const submodules = usePermittedSubmodules(BASE_PATH);

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        {/* Header do modulo */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={5} mb={3}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bg={MODULE_COLOR}
              boxShadow="0 8px 24px -4px rgba(242, 153, 74, 0.25)"
            >
              <MdAssessment size={32} color={MODULE_ICON_COLOR} />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Módulo
              </Text>
              <Heading
                fontSize={{ base: '24px', md: '32px' }}
                fontWeight="700"
                color="#0F172A"
                lineHeight="1.1"
                letterSpacing="-0.02em"
              >
                {MODULE_NAME}
              </Heading>
            </Box>
          </Flex>

          <Text fontSize="sm" color="#71717A" mb={10} ml={1} maxW="640px" lineHeight="1.6">
            {MODULE_DESCRIPTION}
          </Text>
        </motion.div>

        {/* Subtitulo dos submodulos */}
        <Flex align="center" gap={2} mb={5}>
          <Buildings size={18} color="#1B88F5" weight="duotone" />
          <Heading as="h2" fontSize="md" fontWeight="600" color="#0F172A" letterSpacing="-0.01em">
            Submódulos disponíveis
          </Heading>
          <Text fontSize="xs" color="#94A3B8" ml={1}>
            ({submodules.length})
          </Text>
        </Flex>

        {submodules.length === 0 ? (
          <GlassCard intensity="medium" p={10} textAlign="center">
            <Stack align="center" spacing={2}>
              <Text fontSize="md" color="#71717A">
                Nenhum submódulo disponível ainda.
              </Text>
              <Text fontSize="xs" color="#94A3B8">
                Submódulos serão exibidos aqui automaticamente quando criados.
              </Text>
            </Stack>
          </GlassCard>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
            {submodules.map((sub, idx) => (
              <SubmoduleCard key={sub.path} sub={sub} index={idx} />
            ))}
          </SimpleGrid>
        )}
      </Box>
    </GlassPageBg>
  );
}
